/**
 * Utility functions for time duration formatting
 */

/**
 * Format seconds to human-readable duration "8ч 30м"
 * @param seconds - Total seconds
 * @returns Formatted string like "8ч 30м" or "45м" or "30с"
 */
export function formatSeconds(seconds: number): string {
  if (!seconds || seconds < 0) return '0м';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}ч`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}м`);
  }

  // Show seconds only if less than 1 minute
  if (hours === 0 && minutes === 0 && secs > 0) {
    parts.push(`${secs}с`);
  }

  return parts.join(' ') || '0м';
}

/**
 * Format seconds to HH:MM:SS duration format
 * @param seconds - Total seconds
 * @returns Formatted string like "08:30:45"
 */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '00:00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return [hours, minutes, secs]
    .map((val) => String(val).padStart(2, '0'))
    .join(':');
}

/**
 * Format seconds to short HH:MM format (without seconds)
 * @param seconds - Total seconds
 * @returns Formatted string like "08:30"
 */
export function formatDurationShort(seconds: number): string {
  if (!seconds || seconds < 0) return '00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return [hours, minutes]
    .map((val) => String(val).padStart(2, '0'))
    .join(':');
}

/**
 * Format minutes to hours and minutes "2ч 15м"
 * @param minutes - Total minutes
 * @returns Formatted string
 */
export function formatMinutes(minutes: number): string {
  if (!minutes || minutes < 0) return '0м';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}ч`);
  }

  if (mins > 0) {
    parts.push(`${mins}м`);
  }

  return parts.join(' ') || '0м';
}

/**
 * Calculate average seconds per day
 * @param totalSeconds - Total seconds worked
 * @param days - Number of working days
 * @returns Average seconds per day
 */
export function calculateAverageSecondsPerDay(
  totalSeconds: number,
  days: number
): number {
  if (!days || days <= 0) return 0;
  return Math.floor(totalSeconds / days);
}

/**
 * Convert seconds to decimal hours (for reports/charts)
 * @param seconds - Total seconds
 * @returns Hours as decimal (e.g., 8.5 for 8 hours 30 minutes)
 */
export function secondsToDecimalHours(seconds: number): number {
  if (!seconds || seconds < 0) return 0;
  return Number((seconds / 3600).toFixed(2));
}

/**
 * Get color class for overtime (green/yellow/red)
 * @param overtimeSeconds - Overtime seconds
 * @returns Tailwind color class
 */
export function getOvertimeColorClass(overtimeSeconds: number): string {
  if (overtimeSeconds <= 0) return 'text-muted-foreground';
  if (overtimeSeconds < 3600) return 'text-yellow-600'; // < 1 hour
  return 'text-orange-600'; // >= 1 hour
}

/**
 * Get color class for late minutes (yellow/red)
 * @param lateMinutes - Late minutes
 * @returns Tailwind color class
 */
export function getLateColorClass(lateMinutes: number): string {
  if (lateMinutes <= 0) return 'text-muted-foreground';
  if (lateMinutes <= 15) return 'text-yellow-600'; // <= 15 minutes
  return 'text-red-600'; // > 15 minutes
}

/**
 * Расчёт метрик ночной смены
 * Ночные часы по ТК РК: 22:00-06:00
 * @param shiftStart - время начала в формате HH:mm или HH:mm:ss
 * @param shiftEnd - время окончания в формате HH:mm или HH:mm:ss
 * @returns { nightMinutes, dayMinutes, crossesMidnight, totalMinutes }
 */
export function calculateNightShiftMetrics(
  shiftStart: string | null | undefined,
  shiftEnd: string | null | undefined
): {
  nightMinutes: number;
  dayMinutes: number;
  crossesMidnight: boolean;
  totalMinutes: number;
} {
  if (!shiftStart || !shiftEnd) {
    return { nightMinutes: 0, dayMinutes: 0, crossesMidnight: false, totalMinutes: 0 };
  }

  // Парсим время (HH:mm или HH:mm:ss)
  const [startH, startM] = shiftStart.split(':').map(Number);
  const [endH, endM] = shiftEnd.split(':').map(Number);

  const startTotalMinutes = startH * 60 + startM;
  const endTotalMinutes = endH * 60 + endM;

  // Определяем пересечение полуночи: конец меньше начала
  const crossesMidnight = endTotalMinutes < startTotalMinutes;

  // Рассчитываем общую длительность в минутах
  let totalMinutes: number;
  if (crossesMidnight) {
    // Ночная смена: от начала до полуночи + от полуночи до конца
    totalMinutes = 24 * 60 - startTotalMinutes + endTotalMinutes;
  } else {
    totalMinutes = endTotalMinutes - startTotalMinutes;
  }

  // Если длительность отрицательная или нулевая - возвращаем нули
  if (totalMinutes <= 0) {
    return { nightMinutes: 0, dayMinutes: 0, crossesMidnight: false, totalMinutes: 0 };
  }

  // Ночной период: 22:00-06:00 (по ТК РК)
  const NIGHT_START = 22 * 60; // 1320 минут
  const NIGHT_END = 6 * 60; // 360 минут
  const MIDNIGHT = 24 * 60; // 1440 минут

  let nightMinutes = 0;
  let dayMinutes = 0;

  // Итерируем по каждой минуте смены
  let currentMinute = startTotalMinutes;
  let minutesCounted = 0;

  while (minutesCounted < totalMinutes) {
    const normalizedMinute = currentMinute % MIDNIGHT;

    // Ночные: 22:00-23:59 (1320-1439) или 00:00-05:59 (0-359)
    if (normalizedMinute >= NIGHT_START || normalizedMinute < NIGHT_END) {
      nightMinutes++;
    } else {
      dayMinutes++;
    }

    currentMinute++;
    minutesCounted++;
  }

  return { nightMinutes, dayMinutes, crossesMidnight, totalMinutes };
}
